
import React, { useState, useEffect, useRef } from 'react';
import PreservationTechnique from './PreservationTechnique';
import './Sidebar.css';
import './Preserve.css';
import './Buttons.css';
import './Instructions.css';
import './CropProfile.css';
import api from '../api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';

const Preserve = ({
    preserveUserKey,
    handleTabChange,
    handleTryDemo,
    handleCancelDemo,
    isDemoMode,
    currentDate,
    onOpenHelp,
    isLockedOutMode
}) => {
    const [showPreservationInfo, setShowPreservationInfo] = useState(false);
    const [preservableCrops, setPreservableCrops] = useState([]);
    const [pantryItems, setPantryItems] = useState([]);

    const [editingPantryID, setEditingPantryID] = useState(null);
    const [editPantryAmount, setEditPantryAmount] = useState('');
    const [editPantryDate, setEditPantryDate] = useState('');
    const [savingPantryEdit, setSavingPantryEdit] = useState(false);

    const [selectedCrop, setSelectedCrop] = useState(null);
    const [expandedCropId, setExpandedCropId] = useState(null);

    const [cropProfile, setCropProfile] = useState(null);
    const [consumptionOptions, setConsumptionOptions] = useState([]);
    const [selectedConsumption, setSelectedConsumption] = useState(null);
    const [showAllPreserveOptions, setShowAllPreserveOptions] = useState(false);
    const [showConsumptionList, setShowConsumptionList] = useState(true);

    const [preserveOptions, setPreserveOptions] = useState([]);
    const [selectedPreserve, setSelectedPreserve] = useState(null);

    const [preservationTechniques, setPreservationTechniques] = useState([]);
    const [preservationTechnique, setPreservationTechnique] = useState(null);

    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [currentUser, setCurrentUser] = useState(null);

    const [availablePreserveOptions, setAvailablePreserveOptions] = useState([]);
    const [newPreserveID, setNewPreserveID] = useState('');
    const [addingPreserve, setAddingPreserve] = useState(false);

    const [showTechniqueForm, setShowTechniqueForm] = useState(false);
    const [techniqueFormMode, setTechniqueFormMode] = useState('create');
    const [selectedTechniqueForForm, setSelectedTechniqueForForm] = useState(null);

    const pantryNavigationRef = useRef(false);

    // Reset selection details whenever a different crop is selected.
    useEffect(() => {

        if (pantryNavigationRef.current) {
            pantryNavigationRef.current = false;
            return;
        }

        setSelectedConsumption(null);
        setCropProfile(null);
        setConsumptionOptions([]);
        setPreserveOptions([]);
        setSelectedPreserve(null);
        setPreservationTechnique(null);
        setShowAllPreserveOptions(false);
        setShowConsumptionList(true);
        setLoadError('');
    }, [selectedCrop]);


    const loadPantrySidebar = async () => {
        try {
            const response = await api.get('/pantryInfo/sidebar');
            setPantryItems(response.data);
        } catch (error) {
            console.error('Error loading pantry sidebar:', error);
            setLoadError('Unable to load your preserved items.');
        }
    };

    const handleEditPantry = (event, batch) => {
        event.stopPropagation();

        setEditingPantryID(batch.PantryInfoID);
        setEditPantryAmount(String(batch.Amount ?? ''));

        setEditPantryDate(
            batch.DatePreserved
                ? String(batch.DatePreserved).slice(0, 10)
                : ''
        );

        setLoadError('');
    };

    const handleCancelPantryEdit = () => {
        setEditingPantryID(null);
        setEditPantryAmount('');
        setEditPantryDate('');
    };

    const handleSavePantryEdit = async (batch) => {
        if (savingPantryEdit) return;

        if (
            editPantryAmount.trim() === '' ||
            !Number.isFinite(Number(editPantryAmount)) ||
            Number(editPantryAmount) < 0
        ) {
            setLoadError('Please enter a valid pantry amount.');
            return;
        }

        if (!editPantryDate) {
            setLoadError('Please select the preservation date.');
            return;
        }

        setSavingPantryEdit(true);
        setLoadError('');

        try {
            await api.put(`/pantryInfo/${batch.PantryInfoID}`, {
                Amount: editPantryAmount,
                Unit: batch.Unit,
                DatePreserved: editPantryDate
            });

            await loadPantrySidebar();

            setEditingPantryID(null);
            setEditPantryAmount('');
            setEditPantryDate('');
        } catch (error) {
            console.error('Error updating pantry batch:', error);

            setLoadError(
                error.response?.data?.error ??
                'Unable to update the pantry batch.'
            );
        } finally {
            setSavingPantryEdit(false);
        }
    };


    // Load the current user and the available preservable crops.
    useEffect(() => {
        let isMounted = true;

        const loadPreserveData = async () => {
            setLoading(true);
            setLoadError('');

            try {
                const userResponse = await api.get('/users/me');

                if (isMounted) {
                    setCurrentUser(userResponse.data);
                }

                const cropsResponse = await api.get('/preserve/crops');

                if (isMounted) {
                    setPreservableCrops(cropsResponse.data);
                }

                const pantryResponse = await api.get('/pantryInfo/sidebar');

                if (isMounted) {
                    setPantryItems(pantryResponse.data);
                }

            } catch (error) {
                console.error('Error loading Preserve data:', error);

                if (isMounted) {
                    setLoadError(
                        'Unable to load your Preserve information.'
                    );
                    setCurrentUser(null);
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        loadPreserveData();

        return () => {
            isMounted = false;
        };
    }, [preserveUserKey]);

    // Get the crop ID.
    const getCropId = (crop) =>
        crop?.PreservableCropID ??
        crop?.preservableCropID ??
        crop?.id ??
        crop?.PlantID ??
        crop?.plantID;

    // Get the crop name.
    const getCropName = (crop) =>
        crop?.Name ??
        crop?.name ??
        crop?.CropName ??
        crop?.cropName ??
        'Unnamed Crop';

    // Get a description.
    const getDescription = (item) =>
        item?.Description ??
        item?.description ??
        item?.CropDescription ??
        item?.PreserveDescription ??
        item?.PreservationDescription ??
        item?.preservationDescription ??
        '';

    // Get the preservation method ID.
    const getPreserveId = (item) =>
        item?.PreserveID ??
        item?.preserveID ??
        item?.PreserveMethodID ??
        item?.preserveMethodID ??
        item?.PreservationMethodID ??
        item?.preservationMethodID ??
        item?.id ??
        getPreserveName(item);

    // Get the preservation method name.
    const getPreserveName = (item) =>
        item?.Name ??
        item?.name ??
        item?.MethodName ??
        item?.methodName ??
        item?.PreserveMethodName ??
        item?.preserveMethodName ??
        'Preservation Method';

    // Select a crop and load its consumption options.
    const handleCropSelect = async (crop) => {
        setShowTechniqueForm(false);  // Close the open recipe screen

        setSelectedCrop(crop);
        setExpandedCropId(getCropId(crop));

        setSelectedConsumption(null);
        setShowConsumptionList(true);
        setShowAllPreserveOptions(false);
        setSelectedPreserve(null);
        setPreservationTechnique(null);
        setCropProfile(crop);
        setConsumptionOptions([]);
        setPreserveOptions([]);
        setLoadError('');

        const cropId = getCropId(crop);

        if (!cropId) {
            console.error('Missing PreservableCropID for crop:', crop);
            setLoadError('Unable to identify the selected crop.');
            return;
        }

        try {
            const response = await api.get('/preserve/consumption', {
                params: {
                    preservableCropID: cropId
                }
            });

            setConsumptionOptions(response.data);
        } catch (error) {
            console.error('Error loading consumption options:', error);
            setConsumptionOptions([]);
            setLoadError('Unable to load consumption options.');
        }
    };

    // Expand or collapse a crop in the Preserved list.
    const toggleCrop = (crop) => {
        const cropId = getCropId(crop);

        if (expandedCropId === cropId) {
            setExpandedCropId(null);
            return;
        }

        handleCropSelect(crop);
    };

    // Select a consumption option and load its preservation methods.
    const handleConsumptionSelect = async (consumption) => {
        setShowConsumptionList(false);
        setShowAllPreserveOptions(false);

        setSelectedConsumption(consumption);
        setSelectedPreserve(null);

        setPreservationTechnique(null);
        setPreserveOptions([]);
        setLoadError('');

        const cropID = getCropId(selectedCrop);
        const consumptionID = consumption?.ConsumptionID;

        if (!cropID || !consumptionID) {
            setLoadError(
                'Unable to identify the crop or consumption option.'
            );
            return;
        }

        try {

            await loadAvailablePreserveOptions(
                getCropId(selectedCrop),
                consumption.ConsumptionID
            );

            const response = await api.get(
                '/preserve/preservation-methods',
                {
                    params: {
                        preservableCropID: cropID,
                        consumptionID: consumptionID
                    }
                }
            );

            setPreserveOptions(response.data);

        } catch (error) {
            console.error('Error fetching preservation methods:', error);
            setPreserveOptions([]);
            setLoadError('Unable to load preservation methods.');
        }
    };

    // Show every preservation method for the selected crop,
    // without filtering by consumption.
    const handleViewAllPreservationOptions = async () => {
        const cropID = getCropId(selectedCrop);

        if (!cropID) {
            setLoadError('Unable to identify the selected crop.');
            return;
        }

        setShowAllPreserveOptions(true);
        setShowConsumptionList(false);
        setSelectedConsumption(null);
        setSelectedPreserve(null);
        setPreservationTechnique(null);
        setPreservationTechniques([]);
        setPreserveOptions([]);
        setLoadError('');

        try {
            const response = await api.get(
                '/preserve/preservation-methods',
                {
                    params: {
                        preservableCropID: cropID
                    }
                }
            );

            setPreserveOptions(response.data);
        } catch (error) {
            console.error('Error loading all preservation methods:', error);
            setPreserveOptions([]);
            setLoadError('Unable to load all preservation options.');
        }
    };

    const handleViewTechnique = (technique) => {
        setSelectedTechniqueForForm(technique);
        setTechniqueFormMode('view');
        setShowTechniqueForm(true);
    };

    const handleAddNewTechnique = () => {
        setSelectedTechniqueForForm(null);
        setTechniqueFormMode('create');
        setShowTechniqueForm(true);
    };

    const loadAvailablePreserveOptions = async (cropID, consumptionID) => {
        try {
            const response = await api.get('/preserve/available-methods', {
                params: {
                    preservableCropID: cropID,
                    consumptionID
                }
            });

            setAvailablePreserveOptions(response.data);
            setNewPreserveID('');
        } catch (error) {
            console.error('Error loading available preservation methods:', error);
            setAvailablePreserveOptions([]);
            setLoadError('Unable to load available preservation methods.');
        }
    };

    // Select a preservation method and load its techniques.
    const handlePreserveSelect = async (preserve) => {
        setSelectedPreserve(preserve);
        setPreservationTechnique(null);
        setPreservationTechniques([]);
        setLoadError('');

        const preservableCropID = getCropId(selectedCrop);
        const consumptionID = selectedConsumption?.ConsumptionID;
        const preserveID = getPreserveId(preserve);

        if (
            !preservableCropID ||
            !preserveID ||
            (!showAllPreserveOptions && !consumptionID)
        ) {
            setLoadError(
                'Unable to identify the crop or preservation method.'
            );
            return;
        }

        try {
            const params = {
                preservableCropID,
                preserveID
            };

            // Only filter by consumption in the normal workflow.
            if (!showAllPreserveOptions) {
                params.consumptionID = consumptionID;
            }

            const response = await api.get('/preserve/techniques', {
                params
            });

            setPreservationTechniques(response.data);
        } catch (error) {
            console.error('Error loading preservation techniques:', error);
            setPreservationTechniques([]);
            setLoadError('Unable to load preservation techniques.');
        }
    };

    const handleAddPreservationMethod = async () => {
        const preservableCropID = getCropId(selectedCrop);
        const consumptionID = selectedConsumption?.ConsumptionID;
        const preserveID = parseInt(newPreserveID, 10);

        if (!preservableCropID || !consumptionID || !preserveID) {
            return;
        }

        setAddingPreserve(true);
        setLoadError('');

        try {
            await api.post('/preserve/consumption-method', {
                PreservableCropID: preservableCropID,
                ConsumptionID: consumptionID,
                PreserveID: preserveID
            });

            // Refresh the methods displayed in Step 2.
            const methodsResponse = await api.get('/preserve/preservation-methods', {
                params: {
                    preservableCropID,
                    consumptionID
                }
            });

            setPreserveOptions(methodsResponse.data);
            setNewPreserveID('');

            // Refresh the dropdown so the newly added method disappears from it.
            await loadAvailablePreserveOptions(
                preservableCropID,
                consumptionID
            );
        } catch (error) {
            console.error('Error adding preservation method:', error);
            setLoadError('Unable to add the preservation method.');
        } finally {
            setAddingPreserve(false);
        }
    };



    const handlePantryRecipeSelect = async (crop, batch) => {
        const cropID = getCropId(crop);

        const selectedMethod = {
            PreserveID: batch.PreserveID,
            Name: batch.PreserveName
        };

        const technique = {
            PreservationTechniqueID: batch.PreservationTechniqueID,
            Name: batch.TechniqueName,
            Recipe: batch.Recipe,
            CreatedBy: batch.CreatedBy
        };

        // Tell the crop-change effect not to reset this navigation.
        pantryNavigationRef.current = true;

        setSelectedCrop(crop);
        setExpandedCropId(cropID);
        setCropProfile(crop);

        // Restore the "View All Available Preservation Options" state.
        setSelectedConsumption(null);
        setShowAllPreserveOptions(true);
        setShowConsumptionList(false);

        setSelectedPreserve(selectedMethod);
        setPreservationTechnique(null);
        setPreservationTechniques([]);
        setPreserveOptions([]);

        setSelectedTechniqueForForm(technique);
        setTechniqueFormMode('view');
        setLoadError('');

        try {
            const [methodsResponse, techniquesResponse] =
                await Promise.all([
                    api.get('/preserve/preservation-methods', {
                        params: {
                            preservableCropID: cropID
                        }
                    }),

                    api.get('/preserve/techniques', {
                        params: {
                            preservableCropID: cropID,
                            preserveID: batch.PreserveID
                        }
                    })
                ]);

            setPreserveOptions(methodsResponse.data);
            setPreservationTechniques(techniquesResponse.data);
        } catch (error) {
            console.error(
                'Error loading pantry preservation options and recipes:',
                error
            );

            setLoadError(
                'Unable to load the preservation options and recipes.'
            );
        }

        setShowTechniqueForm(true);
    };

    const getPreservedCrops = () => {
        const cropsById = new Map();

        pantryItems.forEach((item) => {
            const cropID = item.PreservableCropID;

            if (cropID == null) return;

            if (!cropsById.has(cropID)) {
                cropsById.set(cropID, {
                    PreservableCropID: cropID,
                    CropName: item.CropName,
                    CropDescription: item.CropDescription
                });
            }
        });

        return Array.from(cropsById.values()).sort((a, b) =>
            (a.CropName || '').localeCompare(b.CropName || '')
        );
    };

    const isInPreserveList = (crop) => {
        const cropID = Number(getCropId(crop));

        return pantryItems.some(
            (item) => Number(item.PreservableCropID) === cropID
        );
    };


    // Webinar and demo information.
    const renderWebinar = () => {
        if (isDemoMode) {
            return (
                <div className="sidebar-demo-bubble demo-active">
                    <div className="demo-active-text">
                        Demo Garden Mode
                    </div>

                    {handleCancelDemo && (
                        <button
                            type="button"
                            className="button small"
                            onClick={handleCancelDemo}
                        >
                            Exit Demo
                        </button>
                    )}
                </div>
            );
        }

        return (
            <div
                className="sidebar-demo-bubble"
                onClick={() =>
                    window.open(
                        'https://live.zohocloud.ca/mmbi-ayx-maw',
                        '_blank',
                        'noopener,noreferrer'
                    )
                }
                title="Join our live GrowYourFoodApp overview webinar!"
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                    if (
                        event.key === 'Enter' ||
                        event.key === ' '
                    ) {
                        event.preventDefault();

                        window.open(
                            'https://live.zohocloud.ca/mmbi-ayx-maw',
                            '_blank',
                            'noopener,noreferrer'
                        );
                    }
                }}
            >
                <div className="demo-title">
                    Webinar Registration
                </div>

                <div className="demo-subline">
                    Live GrowYourFoodApp Overview Session
                </div>
            </div>
        );
    };

    // Current user's profile.
    const renderUserProfile = () => {
        if (!currentUser) return null;

        if (currentUser.isGardener) {
            return (
                <>
                    <h2>Gardener Profile</h2>

                    <div className="gardener-info">
                        <div className="gardener-row">
                            <strong>{currentUser.name || ''}</strong>
                        </div>

                        <div className="gardener-row">
                            {currentUser.experienceLevel || ''} -{' '}
                            {currentUser.city || ''},{' '}
                            {currentUser.province || ''}
                        </div>

                        <div className="gardener-row">
                            {currentUser.email || ''}
                        </div>

                        <div className="gardener-row">
                            Family Size: {currentUser.familysize || ''}
                        </div>

                        <button
                            type="button"
                            className="button small profile-manage-btn"
                            onClick={() => handleTabChange('Gardener')}
                            disabled={isDemoMode}
                        >
                            Manage Profile
                        </button>
                    </div>
                </>
            );
        }

        return (
            <>
                <h2>Shopper Profile</h2>

                <div className="gardener-info">
                    <div className="gardener-row">
                        <strong>{currentUser.name || ''}</strong>
                    </div>

                    <div className="gardener-row">
                        {currentUser.email || ''}
                    </div>
                </div>
            </>
        );
    };

    // Crops the user has added to the local Preserved list.


    // Display the crops the user has preserved, with each pantry batch underneath.

    const renderPreserveList = () => {
        const preservedCrops = getPreservedCrops();

        return (
            <>
                <div className="section-spacer" />
                <h2>Preserved Pantry Items</h2>

                {preservedCrops.length === 0 ? (
                    <p className="no-varieties">
                        Your pantry is empty. Choose a crop from the
                        Preservable Crops library below to get started.
                    </p>
                ) : (
                    <div className="preserve-library">
                        {preservedCrops.map((crop) => {
                            const cropID = getCropId(crop);
                            const isExpanded =
                                Number(expandedCropId) === Number(cropID);

                            const batches = pantryItems.filter(
                                (item) =>
                                    Number(item.PreservableCropID) ===
                                    Number(cropID)
                            );

                            return (
                                <div className="preserved-crop" key={cropID}>
                                    <button
                                        type="button"
                                        className="preserved-crop-header"
                                        onClick={() => toggleCrop(crop)}
                                        aria-expanded={isExpanded}
                                    >
                                        <span className="preserved-crop-name">
                                            {getCropName(crop)}
                                        </span>

                                        <span className="preserved-crop-count">
                                            {batches.length}{' '}
                                            {batches.length === 1
                                                ? 'item'
                                                : 'items'}
                                        </span>

                                        <FontAwesomeIcon
                                            icon={faChevronRight}
                                            className="preserved-crop-chevron"
                                        />
                                    </button>

                                    {isExpanded && (
                                        <div className="preserved-crop-content">



                                            {batches.map((batch) => {
                                                const isEditingBatch =
                                                    editingPantryID === batch.PantryInfoID;

                                                return (
                                                    <div
                                                        className="pantry-batch"
                                                        key={batch.PantryInfoID}
                                                    >
                                                        <div
                                                            className="pantry-batch-details-container"
                                                            role="button"
                                                            tabIndex={0}
                                                            onClick={() =>
                                                                !isEditingBatch &&
                                                                handlePantryRecipeSelect(crop, batch)
                                                            }
                                                            onKeyDown={(event) => {
                                                                if (
                                                                    (event.key === 'Enter' ||
                                                                        event.key === ' ') &&
                                                                    !isEditingBatch
                                                                ) {
                                                                    event.preventDefault();
                                                                    handlePantryRecipeSelect(crop, batch);
                                                                }
                                                            }}
                                                        >
                                                            <div className="pantry-batch-name">
                                                                {batch.PreserveName}
                                                            </div>

                                                            <div className="pantry-batch-recipe">
                                                                {batch.TechniqueName ||
                                                                    'Recipe not recorded'}
                                                            </div>

                                                            {!isEditingBatch && (
                                                                <div className="pantry-batch-details">
                                                                    {batch.Amount} {batch.Unit} -{' '}
                                                                    {batch.DatePreserved
                                                                        ? String(batch.DatePreserved).slice(0, 10)
                                                                        : 'Date not recorded'}
                                                                </div>
                                                            )}
                                                        </div>

                                                        {isEditingBatch ? (
                                                            <div
                                                                className="pantry-batch-edit"
                                                                onClick={(event) => event.stopPropagation()}
                                                            >
                                                                <label>
                                                                    Amount ({batch.Unit})
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        step="any"
                                                                        value={editPantryAmount}
                                                                        onChange={(event) =>
                                                                            setEditPantryAmount(
                                                                                event.target.value
                                                                            )
                                                                        }
                                                                    />
                                                                </label>

                                                                <label>
                                                                    Date Preserved
                                                                    <input
                                                                        type="date"
                                                                        value={editPantryDate}
                                                                        onChange={(event) =>
                                                                            setEditPantryDate(
                                                                                event.target.value
                                                                            )
                                                                        }
                                                                    />
                                                                </label>

                                                                <button
                                                                    type="button"
                                                                    className="button small"
                                                                    disabled={savingPantryEdit}
                                                                    onClick={() =>
                                                                        handleSavePantryEdit(batch)
                                                                    }
                                                                >
                                                                    {savingPantryEdit
                                                                        ? 'Saving...'
                                                                        : 'Save Changes'}
                                                                </button>

                                                                <button
                                                                    type="button"
                                                                    className="button small"
                                                                    disabled={savingPantryEdit}
                                                                    onClick={handleCancelPantryEdit}
                                                                >
                                                                    Cancel
                                                                </button>
                                                            </div>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                className="button small"
                                                                onClick={(event) =>
                                                                    handleEditPantry(event, batch)
                                                                }
                                                            >
                                                                Edit Batch
                                                            </button>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </>
        );
    };

    // Browse preservable crops by category.
    const renderPreserveLibrary = () => {
        const availableCrops = preservableCrops.filter(
            (crop) => !isInPreserveList(crop)
        );

        const categories = [
            'Vegetable',
            'Fruit and Berry',
            'Herb'
        ];

        return (
            <>
                <div className="section-spacer" />

                <h2>Preservable Crops</h2>

                {availableCrops.length === 0 ? (
                    <p className="no-varieties">
                        {preservableCrops.length === 0
                            ? 'Preservable crops will appear here when connected to the database.'
                            : 'All available crops are already preserved.'}
                    </p>
                ) : (
                    <div className="preserve-library">
                        {categories.map((category) => {
                            const categoryCrops = availableCrops.filter(
                                (crop) =>
                                    crop.Category?.toLowerCase() ===
                                    category.toLowerCase()
                            );

                            return (
                                <details
                                    key={category}
                                    className="preserve-category"
                                    open={category === 'Vegetable'}
                                >
                                    <summary className="preserve-category-header">
                                        {category}

                                        <span className="preserve-category-count">
                                            {categoryCrops.length}
                                        </span>
                                    </summary>

                                    {categoryCrops.length > 0 ? (
                                        <div className="all-crops-list">
                                            {categoryCrops.map((crop) => (
                                                <div
                                                    key={
                                                        getCropId(crop) ??
                                                        getCropName(crop)
                                                    }
                                                    className="crop-tab available-crop"
                                                    onClick={() =>
                                                        handleCropSelect(crop)
                                                    }
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faChevronRight}
                                                        className="variety-chevron"
                                                    />

                                                    <span>
                                                        {getCropName(crop)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="no-varieties">
                                            No available{' '}
                                            {category.toLowerCase()} crops.
                                        </p>
                                    )}
                                </details>
                            );
                        })}
                    </div>
                )}
            </>
        );
    };

    // Main Preserve content.
    const renderPreservationContent = () => {
        if (!selectedCrop) {
            return (
                <div className="crop-profile-box">
                    <h3>Preserve Your Harvest</h3>

                    <p>
                        Make the most of every harvest. Select a crop
                        from your Preserved Pantry Items or Preservable
                        Crops list to get started.
                    </p>
                    <p>
                        We will begin with how you typically enjoy it,
                        then explore the best ways to preserve it for
                        that use so you can enjoy it until the next harvest.
                    </p>

                </div>
            );
        }

        return (
            <>
                <div className="preservation-info-section">
                    <button
                        type="button"
                        className="button preservation-info-button"
                        onClick={() => setShowPreservationInfo(!showPreservationInfo)}
                        aria-expanded={showPreservationInfo}
                    >
                        {showPreservationInfo
                            ? 'Hide: Why do preservation methods matter?'
                            : 'Why do preservation methods matter?'}
                    </button>

                    {showPreservationInfo && (
                        <div className="preservation-info-box">
                            <h2>Why do preservation methods matter?</h2>

                            <p>
                                There are several ways to preserve food, but they don't all
                                work the same way.
                            </p>

                            <p>
                                The preservation method you choose can affect the food's
                                <strong> texture, flavour, appearance, nutrition and how easy
                                    it is to use later.</strong>
                            </p>

                            <p>
                                That's why we start with <strong>how you want to enjoy your
                                    harvest</strong>.
                            </p>

                            <p>
                                For example, potatoes can be preserved differently depending
                                on whether you want them later for roasted potatoes, mashed
                                potatoes, soup, French fries or potato powder.
                            </p>

                            <p>
                                <strong>The goal isn't to preserve everything the same way.
                                    The goal is to preserve your harvest in ways that make it
                                    useful to you later.</strong>
                            </p>
                        </div>
                    )}
                </div>

                {/* Step 1: Consumption introduction and all-options button */}
                <div className="crop-profile-box">
                    <h3>
                        What do you usually buy or make with{' '}
                        {getCropName(selectedCrop).toLowerCase()}?
                    </h3>
                    <p>
                        Choose the way you enjoy eating or using them, and discover
                        how to preserve {getCropName(selectedCrop).toLowerCase()} to
                        enjoy your harvest throughout the year.
                    </p>

                    <p className="view-all-preservation-link">
                        Or,{' '}
                        <button
                            type="button"
                            onClick={handleViewAllPreservationOptions}
                        >
                            view all available preservation options
                        </button>.
                    </p>
                </div>

                {/* Show/hide consumption options independently of the active workflow */}
                {!showConsumptionList && (
                    <div className="consumption-list-toggle">
                        <button
                            type="button"
                            className="button"
                            onClick={() => setShowConsumptionList(true)}
                            aria-expanded={false}
                        >
                            Show consumption options
                        </button>
                    </div>
                )}

                {/* Consumption list stays separate and underneath the framed box */}
                {showConsumptionList && (
                    <div className="consumption-list">
                        {consumptionOptions.length > 0 ? (
                            consumptionOptions.map((item) => (
                                <div
                                    key={item.ConsumptionID}
                                    className={
                                        "consumption-item " +
                                        (selectedConsumption &&
                                            selectedConsumption.ConsumptionID === item.ConsumptionID
                                            ? "selected"
                                            : "")
                                    }
                                    onClick={() => handleConsumptionSelect(item)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter" || event.key === " ") {
                                            event.preventDefault();
                                            handleConsumptionSelect(item);
                                        }
                                    }}
                                >
                                    <strong>{item.Name}</strong>

                                    {item.Description && (
                                        <span className="consumption-description">
                                            {" - "}{item.Description}
                                        </span>
                                    )}
                                </div>
                            ))
                        ) : (
                            <p className="no-varieties">
                                No consumption options available.
                            </p>
                        )}
                    </div>
                )}

                {/* Step 2: Choose a preservation method */}
                {(selectedConsumption || showAllPreserveOptions) && (
                    <div className="crop-profile-box">
                        <h3>
                            {showAllPreserveOptions
                                ? `All Preservation Options for ${getCropName(selectedCrop)}`
                                : `Preservation Options for ${getCropName(selectedCrop)} - ${selectedConsumption.Name}`}
                        </h3>

                        {preserveOptions.length > 0 ? (
                            <div className="consumption-list">
                                {preserveOptions.map((method) => (
                                    <div
                                        key={getPreserveId(method)}
                                        className={`consumption-item ${getPreserveId(selectedPreserve) ===
                                                getPreserveId(method)
                                                ? 'selected'
                                                : ''
                                            }`}
                                        onClick={() => handlePreserveSelect(method)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(event) => {
                                            if (
                                                event.key === 'Enter' ||
                                                event.key === ' '
                                            ) {
                                                event.preventDefault();
                                                handlePreserveSelect(method);
                                            }
                                        }}
                                    >
                                        <strong>{getPreserveName(method)}</strong>

                                        {getDescription(method) && (
                                            <span className="consumption-description">
                                                {' - '}
                                                {getDescription(method)}
                                            </span>
                                        )}
                                    </div>

                                ))}
                            </div>
                        ) : (
                            <p className="no-varieties">
                                No preservation methods have been added yet.
                            </p>
                        )}

                        {/* Add another preservation option � consumption mode only */}
                        {!showAllPreserveOptions && (
                            <div className="add-preservation-option">                            <label htmlFor="new-preservation-method">
                                Add another preservation option
                            </label>

                                <select
                                    id="new-preservation-method"
                                    value={newPreserveID}
                                    onChange={(event) =>
                                        setNewPreserveID(event.target.value)
                                    }
                                    disabled={
                                        addingPreserve ||
                                        availablePreserveOptions.length === 0
                                    }
                                >
                                    <option value="">
                                        {availablePreserveOptions.length === 0
                                            ? 'No additional methods available'
                                            : 'Select a preservation option'}
                                    </option>

                                    {availablePreserveOptions.map((method) => (
                                        <option
                                            key={getPreserveId(method)}
                                            value={getPreserveId(method)}
                                        >
                                            {getPreserveName(method)}
                                        </option>
                                    ))}
                                </select>

                                <button
                                    type="button"
                                    className="button small"
                                    onClick={handleAddPreservationMethod}
                                    disabled={!newPreserveID || addingPreserve}
                                >
                                    {addingPreserve ? 'Adding...' : 'Add'}
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Step 3: Choose a preservation technique */}
                {selectedPreserve && (
                    <div className="crop-profile-box">
                        <h3>
                            {getPreserveName(selectedPreserve)} Instructions for{' '}
                            {getCropName(selectedCrop)}
                        </h3>

                        {preservationTechniques.length > 0 ? (
                            <div className="consumption-list preservation-technique-list">
                                {preservationTechniques.map((technique) => (
                                    <div
                                        key={technique.PreservationTechniqueID}
                                        className="consumption-item"
                                        onClick={() => handleViewTechnique(technique)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(event) => {
                                            if (
                                                event.key === 'Enter' ||
                                                event.key === ' '
                                            ) {
                                                event.preventDefault();
                                                handleViewTechnique(technique);
                                            }
                                        }}
                                    >
                                        <strong>{technique.Name}</strong>

                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="no-varieties">
                                No instructions have been added yet.
                            </p>
                        )}

                        <button
                            type="button"
                            className="button small"
                            onClick={handleAddNewTechnique}
                        >
                            Add New
                        </button>
                    </div>
                )}
            </>
        );
    };

    return (
        <>
            {/* Middle content */}
            <div
                className="crop-profile preserve-main"
                style={{
                    flex: '2 1 0',
                    minWidth: 0
                }}
            >
                {showTechniqueForm ? (
                    <PreservationTechnique
                        mode={techniqueFormMode}
                        currentUser={currentUser}
                        technique={selectedTechniqueForForm}
                        selectedCrop={selectedCrop}
                        selectedPreserve={selectedPreserve}
                        onClose={() => setShowTechniqueForm(false)}

                        onSaved={async (savedTechnique) => {

                            const techniqueID =
                                savedTechnique?.PreservationTechniqueID ??
                                savedTechnique?.preservationTechniqueID;

                            if (!techniqueID) {
                                setLoadError(
                                    'The recipe was saved, but the new recipe ID was not returned.'
                                );
                                return;
                            }

                            // Refresh the list of techniques for the selected method.
                            if (selectedPreserve && selectedCrop) {
                                await handlePreserveSelect(selectedPreserve);
                            }

                            // Keep the form open and display the saved recipe.
                            setSelectedTechniqueForForm(savedTechnique);
                            setTechniqueFormMode('view');
                            setShowTechniqueForm(true);
                        }}
                        onPantrySaved={loadPantrySidebar}
                    />
                ) : (
                    <>
                        {loading && (
                            <p>Loading preservation information...</p>
                        )}

                        {loadError && (
                            <div className="crop-profile-box">
                                <p>{loadError}</p>
                            </div>
                        )}

                        {renderPreservationContent()}
                    </>
                )}
            </div>

            {/* Right sidebar */}
            <div
                className="sidebar-right"
                style={{
                    flex: '1 1 0',
                    minWidth: 0
                }}
            >
                <div className="current-gardener">
                    {renderWebinar()}

                    {renderUserProfile()}

                    {renderPreserveList()}

                    {renderPreserveLibrary()}
                </div>

                <div className="section-spacer" />

                <div className="sidebar-footer-links">
                    <button
                        type="button"
                        className="sidebar-link-button"
                        onClick={() => onOpenHelp?.()}
                    >
                        FAQ
                    </button>

                    <button
                        type="button"
                        className="sidebar-link-button"
                        onClick={() => handleTabChange?.('About')}
                    >
                        About
                    </button>
                </div>
            </div>
        </>
    );
};

export default Preserve;
